import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Lock, Loader2, UserPlus, Check, Church } from "lucide-react";
import { categoryIcons, categoryColors } from "./constants";

export default function GroupCard({ group, onJoin, onLeave, joinLoading }) {
  const navigate = useNavigate();
  const Icon = categoryIcons[group.category] || Users;
  const color = categoryColors[group.category] || "#6B665E";

  return (
    <Card 
      className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/groups/${group.group_id}`)}
      data-testid={`group-card-${group.group_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Group Icon */}
          <div 
            className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}15` }}
          >
            {group.image_url ? (
              <img src={group.image_url} alt={group.name} className="w-full h-full object-cover rounded-lg" />
            ) : (
              <Icon className="w-7 h-7" style={{ color }} />
            )}
          </div>
          
          {/* Group Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] text-lg truncate">
                {group.name}
              </h3>
              {group.privacy === "private" && (
                <Lock className="w-3.5 h-3.5 text-[#6B665E] flex-shrink-0" />
              )}
              {group.privacy === "parish_only" && (
                <Church className="w-3.5 h-3.5 text-[#4169E1] flex-shrink-0" />
              )}
            </div>
            
            <p className="text-sm text-[#6B665E] font-['Nunito'] line-clamp-2 mb-2">
              {group.description}
            </p>
            
            <div className="flex items-center gap-3 flex-wrap">
              <Badge 
                className="text-xs font-['Nunito']"
                style={{ backgroundColor: `${color}15`, color, borderColor: color }}
              >
                {group.category}
              </Badge>
              <span className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                <Users className="w-3 h-3" />
                {group.member_count} {group.member_count === 1 ? "member" : "members"}
              </span>
            </div>
          </div>
          
          {/* Join/Leave Button */}
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {group.is_member ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onLeave(group.group_id)}
                disabled={joinLoading === group.group_id}
                className="border-[#E5E0D5] text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:border-[var(--liturgical-primary)]"
                data-testid={`leave-group-btn-${group.group_id}`}
              >
                {joinLoading === group.group_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Joined
                  </>
                )}
              </Button>
            ) : group.has_pending_request ? (
              <Button
                variant="outline"
                size="sm"
                disabled
                className="border-[#D4AF37] text-[#D4AF37]"
              >
                Pending
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => onJoin(group.group_id)}
                disabled={joinLoading === group.group_id}
                className="bg-[#556B2F] hover:bg-[#445624] text-white"
                data-testid={`join-group-btn-${group.group_id}`}
              >
                {joinLoading === group.group_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-1" />
                    {group.privacy === "parish_only" ? "Join" : group.privacy === "private" ? "Request" : "Join"}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
